import { createStore } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { DEFAULT_CHART_CFG } from '../constants';
import {
    ChartConfig,
    ChartDataSource,
    ChartSign,
    ChartSignValue,
    ChartStyle,
    ChartStyleValue,
    ConsolidatedMode,
    DataSourceState,
    SeriesType,
    SeriesTypeValue,
} from '../types';
import {
    findSource,
    cleanupHiddenSeries,
    reconcileDataSources,
    syncActiveTab,
    syncPeriods,
    computeAutoHeadingFromSources,
    applyGlobalSeriesType,
} from './utils';
import { getSeriesKey } from '../utils';

// ─── State types ──────────────────────────────────────────────────────────────

export type ChartStoreState = {
    dataSources: DataSourceState[];
    activeDataSourceTab: number | null;

    availablePeriods: string[];
    selectedPeriods: string[];
    chartCfg: ChartConfig;
    heading: string;
    editingHeading: boolean;
    hiddenSeries: Set<string>;
    showOriginalDataIfLatestIsMissing: boolean;
    descending: boolean;
    consolidatedMode?: ConsolidatedMode;
    autoSelectAllPeriods: boolean;
};

// ─── Action types ─────────────────────────────────────────────────────────────

type FieldActions = {
    changeFieldChartSign: (sourceId: number, fieldId: string, newSign: ChartSignValue) => void;
    toggleFieldChartType: (sourceId: number, fieldId: string) => void;
    toggleFieldAnnotation: (sourceId: number, fieldId: string) => void;
    toggleFieldSeparateAxis: (sourceId: number, fieldId: string) => void;
    setGlobalSeriesType: (seriesType: SeriesTypeValue) => void;
};

type ChartActions = {
    updateChartCfg: (partialConfig: Partial<ChartConfig>) => void;
    setHeading: (heading: string) => void;
    setEditingHeading: (editing: boolean) => void;
    setSelectedPeriods: (periods: string[]) => void;
    setChartStyle: (style: ChartStyleValue) => void;
    setShowOriginalData: (value: boolean) => void;
    setDescending: (value: boolean) => void;
    setAutoSelectAllPeriods: (value: boolean) => void;
};

type SeriesActions = {
    getHiddenSeries: () => Set<string>;
    toggleSeriesVisibility: (key: string) => void;
    isolateSeriesToggle: (key: string, allKeys: string[]) => void;
    resetSeriesVisibility: () => void;
};

type DataSourceActions = {
    syncDataSources: (sources: ChartDataSource[], consolidatedMode?: ConsolidatedMode) => void;
    setActiveTab: (id: number | null) => void;
};

export type ChartStoreActions = {
    fieldActions: FieldActions;
    chartActions: ChartActions;
    seriesActions: SeriesActions;
    dataSourceActions: DataSourceActions;
};

export type ChartStoreType = ChartStoreState & ChartStoreActions;

const DEFAULT_STATE: ChartStoreState = {
    dataSources: [],
    activeDataSourceTab: null,
    availablePeriods: [],
    selectedPeriods: [],
    chartCfg: DEFAULT_CHART_CFG,
    heading: '',
    editingHeading: false,
    hiddenSeries: new Set<string>(),
    showOriginalDataIfLatestIsMissing: false,
    descending: false,
    autoSelectAllPeriods: false,
};

export const createChartStore = (initialState?: Partial<ChartStoreState>) => {
    return createStore<ChartStoreType>()(
        devtools(
            immer((set, get) => ({
                ...DEFAULT_STATE,
                ...initialState,

                fieldActions: {
                    changeFieldChartSign: (sourceId, fieldId, newSign) =>
                        set((state) => {
                            const source = findSource(state.dataSources, sourceId);
                            if (!source?.fieldsCfg[fieldId]) {
                                return;
                            }
                            source.fieldsCfg[fieldId].chartSign = newSign;

                            if (newSign !== ChartSign.OFF && !source.selectedFieldIds.includes(fieldId)) {
                                source.selectedFieldIds.push(fieldId);
                            } else if (newSign === ChartSign.OFF) {
                                source.selectedFieldIds = source.selectedFieldIds.filter((x) => x !== fieldId);
                                state.hiddenSeries.delete(getSeriesKey(sourceId, fieldId));
                            }

                            state.heading = computeAutoHeadingFromSources(state.dataSources, state.consolidatedMode);
                        }),

                    toggleFieldChartType: (sourceId, fieldId) =>
                        set((state) => {
                            const field = findSource(state.dataSources, sourceId)?.fieldsCfg[fieldId];
                            if (!field) {
                                return;
                            }
                            field.seriesType = field.seriesType === SeriesType.BARS ? SeriesType.LINE : SeriesType.BARS;
                        }),

                    toggleFieldAnnotation: (sourceId, fieldId) =>
                        set((state) => {
                            const field = findSource(state.dataSources, sourceId)?.fieldsCfg[fieldId];
                            if (!field) {
                                return;
                            }
                            field.annotationsEnabled = !field.annotationsEnabled;
                        }),

                    toggleFieldSeparateAxis: (sourceId, fieldId) =>
                        set((state) => {
                            const field = findSource(state.dataSources, sourceId)?.fieldsCfg[fieldId];
                            if (!field) {
                                return;
                            }
                            field.separateAxis = !field.separateAxis;
                        }),

                    setGlobalSeriesType: (seriesType) =>
                        set((state) => {
                            applyGlobalSeriesType(state.dataSources, seriesType);
                        }),
                },

                chartActions: {
                    updateChartCfg: (partialConfig) =>
                        set((state) => {
                            state.chartCfg = { ...state.chartCfg, ...partialConfig };
                        }),

                    setHeading: (heading) =>
                        set((state) => {
                            state.heading = heading;
                        }),

                    setEditingHeading: (editing) =>
                        set((state) => {
                            state.editingHeading = editing;
                        }),

                    setSelectedPeriods: (periods) =>
                        set((state) => {
                            state.selectedPeriods = periods;
                        }),

                    setChartStyle: (style) =>
                        set((state) => {
                            state.chartCfg.style = style;
                            const seriesType: SeriesTypeValue =
                                style === ChartStyle.Line ? SeriesType.LINE : SeriesType.BARS;
                            applyGlobalSeriesType(state.dataSources, seriesType);
                        }),

                    setShowOriginalData: (value) =>
                        set((state) => {
                            state.showOriginalDataIfLatestIsMissing = value;
                        }),

                    setDescending: (value) =>
                        set((state) => {
                            state.descending = value;
                        }),

                    setAutoSelectAllPeriods: (value) =>
                        set((state) => {
                            state.autoSelectAllPeriods = value;
                        }),
                },

                seriesActions: {
                    getHiddenSeries: () => get().hiddenSeries,

                    toggleSeriesVisibility: (key) =>
                        set((state) => {
                            if (state.hiddenSeries.has(key)) {
                                state.hiddenSeries.delete(key);
                            } else {
                                state.hiddenSeries.add(key);
                            }
                        }),

                    isolateSeriesToggle: (key, allKeys) =>
                        set((state) => {
                            const visibleKeys = allKeys.filter((k) => !state.hiddenSeries.has(k));
                            const isIsolated = visibleKeys.length === 1 && visibleKeys[0] === key;

                            if (isIsolated) {
                                state.hiddenSeries.clear();
                            } else {
                                state.hiddenSeries.clear();
                                for (const k of allKeys) {
                                    if (k !== key) {
                                        state.hiddenSeries.add(k);
                                    }
                                }
                            }
                        }),

                    resetSeriesVisibility: () =>
                        set((state) => {
                            state.hiddenSeries.clear();
                        }),
                },

                dataSourceActions: {
                    syncDataSources: (sources, consolidatedMode) =>
                        set((state) => {
                            const newIds = new Set(sources.map((s) => s.id));
                            state.consolidatedMode = consolidatedMode;

                            cleanupHiddenSeries(state.hiddenSeries, state.dataSources, newIds);
                            state.dataSources = reconcileDataSources(sources, state.dataSources, consolidatedMode);
                            state.activeDataSourceTab = syncActiveTab(
                                state.activeDataSourceTab,
                                newIds,
                                state.dataSources,
                            );
                            state.heading = computeAutoHeadingFromSources(state.dataSources, state.consolidatedMode);
                            const periodsResult = syncPeriods(state.selectedPeriods, state.dataSources, state.autoSelectAllPeriods);
                            state.availablePeriods = periodsResult.availablePeriods;
                            state.selectedPeriods = periodsResult.selectedPeriods;
                        }),

                    setActiveTab: (id) =>
                        set((state) => {
                            state.activeDataSourceTab = id;
                        }),
                },
            })),
        ),
    );
};
